import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Edit2, Shield, UserCog } from 'lucide-react';
import toast from 'react-hot-toast';
import { Modal } from '../components/Modal';
import { Pagination } from '../components/Pagination';
import { rbacService, type AdminAccount, type Role } from '../services/rbacService';
import { fieldClass, labelClass } from '../utils/form';
import { rowNumber } from '../utils/rowNumber';
import { useAuth } from '../context/AuthContext';

type AdminForm = {
  name: string;
  email: string;
  phone: string;
  password: string;
  role: string;
  isActive: boolean;
};

const emptyForm = (role = 'admin'): AdminForm => ({
  name: '',
  email: '',
  phone: '',
  password: '',
  role,
  isActive: true,
});

export function AdminsPage() {
  const { user, hasPermission } = useAuth();
  const canWrite = hasPermission('admins.write') || user?.role === 'super_admin';

  const [currentPage, setCurrentPage] = useState(1);
  const [admins, setAdmins] = useState<AdminAccount[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<AdminAccount | null>(null);
  const [form, setForm] = useState<AdminForm>(emptyForm());
  const itemsPerPage = 10;

  const roleOptions = useMemo(() => {
    const list = roles.filter((r) => r.slug !== 'super_admin' || user?.role === 'super_admin');
    return list.length ? list : [{ id: 'admin', slug: 'admin', name: 'مدیر' } as Role];
  }, [roles, user?.role]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [adminRes, roleList] = await Promise.all([
        rbacService.getAdmins({ page: currentPage, limit: itemsPerPage }),
        rbacService.listRoles(),
      ]);
      setAdmins(adminRes.data ?? []);
      setTotalPages(Math.max(1, adminRes.totalPages));
      setRoles(roleList);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در دریافت ادمین‌ها');
      setAdmins([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  useEffect(() => {
    void load();
  }, [load]);

  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
    setForm(emptyForm(roleOptions[0]?.slug ?? 'admin'));
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm(roleOptions[0]?.slug ?? 'admin'));
    setFormOpen(true);
  };

  const openEdit = (row: AdminAccount) => {
    setEditing(row);
    setForm({
      name: row.name,
      email: row.email,
      phone: row.phone,
      password: '',
      role: row.role || 'admin',
      isActive: row.isActive,
    });
    setFormOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error('نام و ایمیل الزامی است');
      return;
    }
    if (!editing && form.password.length < 8) {
      toast.error('رمز عبور باید حداقل ۸ کاراکتر باشد');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await rbacService.updateAdmin(editing.id, {
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          role: form.role,
          isActive: form.isActive,
          ...(form.password ? { password: form.password } : {}),
        });
        toast.success('ادمین به‌روزرسانی شد');
      } else {
        await rbacService.createAdmin({
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          password: form.password,
          role: form.role,
        });
        toast.success('ادمین جدید ایجاد شد');
      }
      closeForm();
      void load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در ذخیره');
    } finally {
      setSaving(false);
    }
  };

  const roleLabel = (slug: string, name?: string) => {
    if (name) return name;
    return roles.find((r) => r.slug === slug)?.name ?? slug;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title text-xl font-bold text-slate-900 sm:text-2xl flex items-center gap-2">
            <UserCog className="h-7 w-7 text-indigo-600" />
            مدیریت ادمین‌ها
          </h1>
          <p className="text-slate-600 mt-1 text-sm">ایجاد و مدیریت حساب‌های پنل مدیریت</p>
        </div>
        {canWrite && (
          <button
            type="button"
            onClick={openCreate}
            className="w-full sm:w-auto inline-flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            ادمین جدید
          </button>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="admin-table-scroll">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-right font-medium text-slate-600">#</th>
                <th className="px-4 py-3 text-right font-medium text-slate-600">نام</th>
                <th className="px-4 py-3 text-right font-medium text-slate-600">ایمیل</th>
                <th className="px-4 py-3 text-right font-medium text-slate-600">نقش</th>
                <th className="px-4 py-3 text-right font-medium text-slate-600">وضعیت</th>
                <th className="px-4 py-3 text-right font-medium text-slate-600">تاریخ ایجاد</th>
                {canWrite && <th className="px-4 py-3 text-right font-medium text-slate-600">عملیات</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={canWrite ? 7 : 6} className="px-4 py-8 text-center text-slate-500">
                    در حال بارگذاری...
                  </td>
                </tr>
              ) : admins.length === 0 ? (
                <tr>
                  <td colSpan={canWrite ? 7 : 6} className="px-4 py-8 text-center text-slate-500">
                    ادمینی یافت نشد
                  </td>
                </tr>
              ) : (
                admins.map((admin, idx) => (
                  <tr key={admin.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-slate-500">{rowNumber(currentPage, itemsPerPage, idx)}</td>
                    <td className="px-4 py-3 font-medium text-slate-900">{admin.name}</td>
                    <td className="px-4 py-3 text-slate-600" dir="ltr">
                      {admin.email}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-xs">
                        <Shield className="h-3 w-3" />
                        {roleLabel(admin.role, admin.roleName)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs ${
                          admin.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {admin.isActive ? 'فعال' : 'غیرفعال'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs">
                      {admin.createdAt ? new Date(admin.createdAt).toLocaleDateString('fa-IR') : '—'}
                    </td>
                    {canWrite && (
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => openEdit(admin)}
                          className="p-1.5 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg"
                          title="ویرایش"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {!loading && totalPages > 1 && (
          <div className="px-4 py-3 border-t border-slate-200">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
          </div>
        )}
      </div>

      <Modal isOpen={formOpen} onClose={closeForm} title={editing ? 'ویرایش ادمین' : 'ادمین جدید'}>
        <div className="space-y-4">
          <div>
            <label className={labelClass}>نام</label>
            <input
              className={fieldClass}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>
          <div>
            <label className={labelClass}>ایمیل</label>
            <input
              type="email"
              dir="ltr"
              className={fieldClass}
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </div>
          <div>
            <label className={labelClass}>موبایل</label>
            <input
              dir="ltr"
              className={fieldClass}
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
          </div>
          <div>
            <label className={labelClass}>نقش</label>
            <select
              className={fieldClass}
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
            >
              {roleOptions.map((r) => (
                <option key={r.slug} value={r.slug}>
                  {r.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>
              {editing ? 'رمز عبور جدید (اختیاری)' : 'رمز عبور'}
            </label>
            <input
              type="password"
              dir="ltr"
              className={fieldClass}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder={editing ? 'برای تغییر وارد کنید' : ''}
            />
          </div>
          {editing && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                className="rounded border-slate-300"
              />
              <span className="text-sm text-slate-700">حساب فعال است</span>
            </label>
          )}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="flex-1 bg-indigo-600 text-white py-2 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? 'در حال ذخیره...' : 'ذخیره'}
            </button>
            <button
              type="button"
              onClick={closeForm}
              className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-50"
            >
              انصراف
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
